import { defaultsTo } from '../utils/utilsGlobal';
import {
  fragmentIssueFull,
  fragmentIssueBasic,
  fragmentMessageBasic,
  fragmentRoomBasic,
  fragmentUserBasic,
  fragmentTask,
  serializeIssueFull,
  serializeIssueBasic,
  serializeMessageBasic,
  serializeRoomBasic,
  serializeUserBasic,
  serializeTask,
} from './serializers';

import {
  gql,
  sendAPIRequest,
  simplifyGraphQLJSON,
  insertGraphQLFragments,
} from './GraphQLClient';


export function getIssueById(companyId, issueId) {
  const query = insertGraphQLFragments(gql`
    query ($companyId: Uuid!, $issueId: ID!) {
      issue(companyId: $companyId, issueId: $issueId) {
        ...fragmentIssueFull
      }
    }
  `, { fragmentIssueFull });
  return sendAPIRequest(query, { companyId, issueId }).then(serializeIssueFull);
}


export function getIssues(companyId, searchQuery, filters, limit = 100, beforeCursor = null) {
  const queryText = searchQuery || '';
  const isClosed = typeof filters?.closed === 'boolean' ? filters?.closed : null;
  const linkedTo = filters?.linkedTo ? [{ providerId: filters.linkedTo, provider: 'JIRA' }] : [];
  const sort = filters?.sort?.toUpperCase?.() || 'IMPORTANT';
  const variables = {
    queryText,
    linkedTo,
    isClosed,
    sort,
    limit,
    beforeCursor,
    companyId,
  };
  const query = insertGraphQLFragments(gql`
    query ($companyId: Uuid!, $queryText: String, $isClosed: Boolean, $linkedTo: [InputTask], $sort: IssueSort, $limit: Int, $beforeCursor: String) {
      issues(companyId: $companyId, query: $queryText, isClosed: $isClosed, linkedWithTasks: $linkedTo, sort: $sort, first: $limit, after: $beforeCursor) {
        edges {
          cursor
          node {
            ...fragmentIssueBasic
          }
        }
      }
    }
  `, { fragmentIssueBasic });
  return sendAPIRequest(query, variables).then(issues => {
    return simplifyGraphQLJSON(issues).map(serializeIssueBasic);
  });
}


export function getIssueLinkedMessages(issueId, roomId, limit = 100) {
  const query = insertGraphQLFragments(gql`
    query ($roomId: ID!, $issueId: ID!, $limit: Int) {
      messages(roomId: $roomId, issueId: $issueId, first: $limit) {
        edges {
          cursor
          node {
            ...fragmentMessageBasic
          }
        }
      }
    }
  `, { fragmentMessageBasic });
  return sendAPIRequest(query, { issueId, roomId, limit }).then(messages => {
    return simplifyGraphQLJSON(messages).map(serializeMessageBasic);
  });
}


export function getIssueLinkedRooms(companyId, issueId, limit = 100) {
  const query = insertGraphQLFragments(gql`
    query ($companyId: Uuid!, $issueId: ID!, $limit: Int!) {
      rooms(companyId: $companyId, issueId: $issueId, first: $limit) {
        edges {
          cursor
          node {
            ...fragmentRoomBasic

            messages(first: 1, issueId: $issueId) {
              count
              edges {
                node { ...fragmentMessageBasic }
              }
            }
          }
        }
      }
    }
  `, { fragmentMessageBasic, fragmentRoomBasic });

  return sendAPIRequest(query, { companyId, issueId, limit }).then(rooms => {
    return simplifyGraphQLJSON(rooms).map(room => {
      return {
        ...serializeRoomBasic(room),
        roomLinkedMessages: simplifyGraphQLJSON(room.messages).map(serializeMessageBasic),
        roomLinkedMessagesCount: room.messages?.count || 0,
      };
    });
  });
}


export function getIssueSentMassMessages(companyId, issueId, limit = 100) {
  const query = insertGraphQLFragments(gql`
    query ($companyId: Uuid!, $issueId: ID!, $limit: Int) {
      issueMassMessages(companyId: $companyId, issueId: $issueId, first: $limit) {
        edges {
          cursor
          node {
            ...fragmentMessageBasic
          }
        }
      }
    }
  `, { fragmentMessageBasic });
  return sendAPIRequest(query, { companyId, issueId, limit }).then(messages => {
    return simplifyGraphQLJSON(messages).map(serializeMessageBasic);
  });
}


export function getIssueHistory(companyId, issueId, limit = 100) {
  const query = insertGraphQLFragments(gql`
    query ($companyId: Uuid!, $issueId: ID!, $limit: Int!) {
      issueHistory(companyId: $companyId, issueId: $issueId, first: $limit) {
        edges {
          cursor
          node {
            id
            status
            timestamp
            task { ...fragmentTask }
            changedBy { ...fragmentUserBasic }
            massMessage { ...fragmentMessageBasic }
          }
        }
      }
    }
  `, { fragmentUserBasic, fragmentMessageBasic, fragmentTask });

  return sendAPIRequest(query, { companyId, issueId, limit }).then(history => {
    return simplifyGraphQLJSON(history).map(item => {
      return {
        ...defaultsTo(item, {
          id: null,
          cursor: null,
          status: null,
          timestamp: null,
        }),
        task: serializeTask(item.task),
        changedBy: serializeUserBasic(item.changedBy),
        massMessage: serializeMessageBasic(item.massMessage),
      };
    });
  });
}


export function updateIssue(companyId, issueId, { title, notes, isClosed }) {
  const query = insertGraphQLFragments(gql`
    mutation ($companyId: Uuid!, $issueId: ID!, $isClosed: Boolean, $notes: String, $title: String) {
      updateIssue(companyId: $companyId, issueId: $issueId, isClosed: $isClosed, notes: $notes, title: $title) {
        ...fragmentIssueFull
      }
    }
  `, { fragmentIssueFull });
  const variables = {
    companyId,
    issueId,
    title,
    notes,
    isClosed,
  };
  return sendAPIRequest(query, variables).then(serializeIssueFull);
}


export function createIssue(companyId, title, messageId) {
  const query = insertGraphQLFragments(gql`
    mutation ($companyId: Uuid!, $title: String!, $messageId: ID!) {
      createIssue(companyId: $companyId, title: $title, messageId: $messageId) {
        ...fragmentIssueBasic
      }
    }
  `, { fragmentIssueBasic });
  const variables = { companyId, title, messageId };
  return sendAPIRequest(query, variables).then(serializeIssueBasic);
}


export function linkMessageWithIssue(issueId, messageId) {
  const query = insertGraphQLFragments(gql`
    mutation ($issueId: ID!, $messageId: ID!) {
      addMessageToIssue(issueId: $issueId, messageId: $messageId) {
        ...fragmentIssueBasic
      }
    }
  `, { fragmentIssueBasic });
  return sendAPIRequest(query, { issueId, messageId }).then(serializeIssueBasic);
}


export function unlinkMessageFromIssue(issueId, messageId) {
  const query = gql`
    mutation ($issueId: ID!, $messageId: ID!) {
      deleteMessageFromIssue(issueId: $issueId, messageId: $messageId) {
        id
      }
    }
  `;
  return sendAPIRequest(query, { issueId, messageId });
}


export function linkJiraTaskWithIssue(companyId, issueId, taskId) {
  const query = insertGraphQLFragments(gql`
    mutation ($companyId: Uuid!, $issueId: ID!, $taskId: ID!) {
      addIssueTask(companyId: $companyId, issueId: $issueId, providerId: $taskId, provider: JIRA) {
        ...fragmentTask
      }
    }
  `, { fragmentTask });
  return sendAPIRequest(query, { companyId, issueId, taskId }).then(serializeTask);
}


export function unlinkJiraTaskFromIssue(companyId, issueId, taskId) {
  const query = gql`
    mutation ($companyId: Uuid!, $taskId: ID!, $issueId: ID!) {
      unlinkIssueTask(companyId: $companyId, taskId: $taskId, issueId: $issueId) {
        id
      }
    }
  `;
  return sendAPIRequest(query, { companyId, issueId, taskId });
}


export function sendIssueMassMessage(companyId, issueId, params) {
  const text = params.text || '';
  const mustOpenWidget = Boolean(params.mustOpenWidget) || false;
  const mentions = params.mentions || 'ALL';
  const attachments = [];
  const binaries = {};

  try {
    params.attachments.forEach(({ file }) => {
      attachments.push(file.name);
      binaries[file.name] = file;
    });
  }
  catch (e) {}

  const variables = {
    companyId,
    issueId,
    text,
    mentions,
    mustOpenWidget,
    attachments,
  };
  const query = insertGraphQLFragments(gql`
    mutation ($companyId: Uuid!, $issueId: ID!, $text: String, $attachments: [Upload], $mustOpenWidget: Boolean, $mentions: MassMessageMention) {
      sendIssueMassMessage(companyId: $companyId, issueId: $issueId, text: $text, attachments: $attachments, mustOpenWidget: $mustOpenWidget, mentions: $mentions) {
        ...fragmentMessageBasic
      }
    }
  `, { fragmentMessageBasic });
  return sendAPIRequest(query, variables, binaries).then(serializeMessageBasic);
}
